import React from "react";
import "./drawer.scss";
import classNames from 'classnames';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ButtonNew } from "components/buttonNew";
import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import { createPortal } from "react-dom";

interface ISecondaryAction {
  label: string;
  action: () => void;
  buttonType?: "primary" | "secondary" | "text";
  disabled?: boolean;
}

interface IProps {
  open: boolean;
  title: string;
  subtitle?: string;
  headerLeading?: React.ReactNode;
  className?: string;
  onClose: () => void;
  onOk: (e?: React.MouseEvent) => void;
  onCancel?: () => void;
  okButtonType?: "primary" | "secondary" | "text";
  cancelButtonType?: "primary" | "secondary" | "text";
  secondaryActions?: Array<ISecondaryAction>;
  okText?: string;
  okDisabled?: boolean;
  /**Close the drawer on backdrop click*/
  backDropClose?: boolean;
  /**If true, allows background elements to be interactable */
  hideBackdrop?: boolean;
  children?: React.ReactNode;
  /**Show cancel button in footer*/
  showCancel?: boolean;
  cancelText?: string;
  /**Custom style for the drawer*/
  style?: React.CSSProperties;
  /* Optional element to display next to close 'x' button */
  headerActions?: React.ReactNode;
}

interface ISectionProps {
  bordered?: boolean;
  children: React.ReactNode;
}

function Drawer(props: IProps) {
  const {
    open,
    title,
    subtitle,
    headerLeading,
    className,
    onClose,
    backDropClose = false,
    hideBackdrop = false,
    onOk,
    onCancel,
    okText = "Confirm",
    okDisabled = false,
    showCancel = false,
    cancelText = "Cancel",
    secondaryActions,
    okButtonType = "primary",
    cancelButtonType = "secondary",
    headerActions,
    children,
    style,
  } = props;

  return createPortal(
    <>
      {/* Drawer */}
      <div className={classNames("ui-drawer", className, { "ui-drawer-active": open })} style={style}>
        <div className="ui-drawer-header">
          <div className="ui-drawer-header-content">
            {headerLeading ? (
              <div className="ui-drawer-header-leading">{headerLeading}</div>
            ) : null}
            <div className="ui-drawer-header-text">
              <h1>{title}</h1>
              {subtitle ? (
                <p className="ui-drawer-subtitle">{subtitle}</p>
              ) : null}
            </div>
          </div>
          <div className="ui-drawer-header-actions">
            {headerActions}
            <FontAwesomeIcon icon={faXmark} onClick={onClose} className="ui-drawer-header-close" />
          </div>
        </div>
        <div className="ui-drawer-body">
          {children}
        </div>
        <div className="ui-drawer-footer">
          {showCancel && (
            <ButtonNew type={cancelButtonType ?? "secondary"} size="medium" block onClick={onCancel || onClose}>
              {cancelText}
            </ButtonNew>
          )}

          {secondaryActions ? secondaryActions?.map((action, index) => (
            <ButtonNew key={index} type={action.buttonType ?? "secondary"} size="medium" block onClick={action.action} disabled={action.disabled}>
              {action.label}
            </ButtonNew>
          )) : null}


          <ButtonNew type={okButtonType ?? "primary"} size="medium" block onClick={onOk} disabled={okDisabled}>
            {okText}
          </ButtonNew>
        </div>
      </div>

      {/* Backdrop */}
      {hideBackdrop ? null : (
        <div
          className={classNames("ui-drawer-backdrop", { "ui-drawer-backdrop-active": open })}
          onClick={backDropClose ? () => onClose() : undefined}
        ></div>
      )}
    </>,
    document.body,
  );
}

function Section(props: ISectionProps) {
  const { bordered, children } = props;
  return (
    <div className={classNames("ui-drawer-section", { "ui-drawer-section-bordered": bordered })}>
      {children}
    </div>
  );
}

Section.displayName = "Section";
Drawer.Section = Section;

export default Drawer;